// Re-render Optimization 14.05.2020

import React, { useState, useRef } from 'react'
import moment from 'moment'
import { Link } from 'react-router-dom'
import LazyLoad from 'react-lazyload'
import _get from 'lodash/get'
import _map from 'lodash/map'

import DateLabel from '../../_library/DateLabel'
import Address from '../../_library/Address'
import { ROLES } from '../../constants/roles'

import EventTicketStatistics from './EventTicketStatistics'
import EventImagePlaceholder from './EventImagePlaceholder'
import { useEffect } from 'react'
import { getEventAlternativeViews } from '../../../_common/core/http_services'
import Clipboard from '../../_library/Clipboard'
import { showAxiosError } from '../../utils/messenger'
import { useMediaQuery } from '../../../_common/core/utils'

const EventRow = props => {
  const { event, autoRefresh, dynamicConfigs, configs, promoterLink, isMobile, isHome, user } = props
  const promoterLinkSplit = (promoterLink || '').split('/')
  const promoterId = promoterLinkSplit[promoterLinkSplit.length - 1]
  const showEventAlternativeViews = _get(configs, 'appearance.showEventAlternativeViews', true)
  const [alternativeViews, setAlternativeViews] = useState([])
  const isMounted = useRef(true)
  const hasFetched = useRef(false)
  const eventUrl = _get(configs, 'behavior.eventUrl', '')
  const showAlternativeView = !!_get(dynamicConfigs, 'show_alternative_view', false)

  const renderAlternativeView = promoterId =>
    _map(alternativeViews, (item, j) => (
      <tr id={j} key={j}>
        <td className="title-alternative-view">{item?.title ?? ''}</td>
        <td className="title-alternative-view">{item?.slug ?? ''}</td>
        <td className="title-alternative-view">{item?.referenceDescription ?? ''}</td>
        <td className="title-alternative-view">
          <Clipboard
            text={`${eventUrl}${item.slug}?r=${promoterId}`}
            containerStyle={{ display: 'inline-flex', padding: 0, border: 'none' }}
            containerClassName="btn event-button-sm"
            isMobile={isMobile}
          >
            <div className="btn event-button-sm" style={{ backgroundColor: '#7784aa', width: '100%' }}>
              <i className="fa fa-files-o" aria-hidden="true" />
              <span className="promoter-link-text">Promoter Link</span>
            </div>
          </Clipboard>
        </td>
      </tr>
    ))

  useEffect(() => {
    const getViews = async () => {
      try {
        if (hasFetched.current) return
        hasFetched.current = true
        const alternativeViews = await getEventAlternativeViews(event.id)
        if (isMounted.current) {
          setAlternativeViews(alternativeViews)
        }
      } catch (error) {
        showAxiosError(error)
      }
    }
    if (
      showAlternativeView &&
      showEventAlternativeViews &&
      event.hasAlternativeViews &&
      promoterId &&
      (ROLES.is_tf_support(user?.tfRole) || event?.self?.role === 'admin' || event?.self?.role === 'owner') &&
      !isHome
    ) {
      getViews()
    }
  }, [
    showAlternativeView,
    showEventAlternativeViews,
    event.id,
    promoterId,
    user,
    event?.self?.role,
    isHome,
    event.hasAlternativeViews,
  ])

  useEffect(
    () => () => {
      isMounted.current = false
    },
    [],
  )

  const address = !!event ? event.venue : null
  const organization = !!event ? event.owner.displayName : null
  const { isOnlineEvent } = event
  const eventId = event.id

  const permission = !!event ? event.self?.role : ''
  const organization_link = !!event ? '/brand/' + event.owner.id + '/details' : '/brands'
  let redirectPath = '/performance'

  if (permission === 'onsite') {
    redirectPath = '/details'
  }
  if (permission === 'curator') {
    redirectPath = '/invitations'
  }

  if (ROLES.is_support_or_partial_support(permission)) {
    redirectPath = '/orders'
  }

  const startDate = moment(new Date(event.startDate))
  const startDate_utc = moment.utc(new Date(event.startDate))
  const now = new Date()
  const cs = [
    startDate.isBefore(now) ? 'row-stale eventslist-row ' : 'eventslist-row',
    event.pinned ? 'event-pinned' : 'event-unpinned',
  ].join(' ')

  const renderStatisticsContent = () => (
    <>
      <div className={isHome ? 'events-list-event-stats-block' : 'event-actions-container'}>
        {permission !== 'curator' && !(permission === ROLES.PARTIAL_SUPPORT) && (
          <EventTicketStatistics event={event} autoRefresh={autoRefresh} isHome={isHome} />
        )}
        {isHome ? null : (
          <div className="eventslist-status">
            {props.children}
            {alternativeViews && alternativeViews.length > 0 && promoterId && (
              <div className="col-12 col-md-12">
                <div className="card-title card-unchanged small-width">Additional Pages</div>
                <div>
                  <table className="table alternative-view-table">
                    <thead>
                      <tr>
                        <th className="title-alternative-view">
                          <strong>Title</strong>
                        </th>
                        <th className="title-alternative-view">
                          <strong>URL</strong>
                        </th>
                        <th className="title-alternative-view">
                          <strong>Purpose</strong>
                        </th>
                        <th className="title-alternative-view">
                          <strong>Promoter Link</strong>
                        </th>
                      </tr>
                    </thead>
                    <tbody>{renderAlternativeView(promoterId)}</tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      {isHome ? <div className="eventslist-status-home">{props.children}</div> : null}
    </>
  )

  const isMobileHook = useMediaQuery('(max-width: 600px)')
  const isMobileHookAndIsHome = isMobileHook && isHome
  const isDesktopAndIsHome = !isMobileHook && isHome

  const renderContent = () => (
    <div className={isHome ? 'events-list-item-container' : 'react-table'}>
      {isDesktopAndIsHome ? (
        <div
          id="image-container-big"
          className={isHome ? 'LazyLoad events-list-details-image-container' : 'LazyLoad'}
          style={{
            backgroundImage: `url(${event.imageURL})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        >
          <LazyLoad width={150} height={150} once>
            {!!event.imageURL ? (
              <Link to={'/event/' + eventId + redirectPath}>
                <img
                  alt="nodata"
                  className={`LazyLoadImg big fdf`}
                  src={event.imageURL}
                  style={{
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                  }}
                />
              </Link>
            ) : (
              <EventImagePlaceholder />
            )}
          </LazyLoad>
        </div>
      ) : null}
      <div className={isHome ? 'events-list-details-container' : 'eventslist-thumbs'}>
        <div
          id="image-container-small"
          className={isHome ? 'LazyLoad events-list-details-image-container' : 'LazyLoad'}
          style={
            isMobileHookAndIsHome
              ? {
                backgroundImage: `url(${event.imageURL})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }
              : isDesktopAndIsHome
              ? {
                display: 'none',
              }
              : {}
          }
        >
          <LazyLoad width={150} height={150} once>
            {!!event.imageURL ? (
              <Link to={'/event/' + eventId + redirectPath}>
                <img
                  alt="nodata"
                  className={`LazyLoadImg ${isHome ? 'small' : ''}`}
                  src={event.imageURL}
                  style={
                    isMobileHookAndIsHome
                      ? {
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                      }
                      : {}
                  }
                />
              </Link>
            ) : (
              <EventImagePlaceholder />
            )}
          </LazyLoad>
        </div>
        <div className={isHome ? 'events-list-event-details-block' : 'event-details'}>
          <div className={isHome ? 'brand-details-block' : 'event-brand'}>
            <Link to={organization_link}>{organization}</Link>
          </div>
          <div className={isHome ? 'event-name-block' : 'event-name'}>
            <Link to={'/event/' + eventId + redirectPath}>{event.displayName}</Link>
          </div>
          <div className={isHome ? 'event-date-block' : 'event-time'}>
            <img alt="nodata" src={asset('/resources/images/cal-ico.svg')} />
            <DateLabel className="starttime" value={startDate_utc} format="LLL" />
          </div>
          {isOnlineEvent ? (
            <div className={isHome ? 'event-address-block' : 'event-address'}>
              <img alt="nodata" src={asset('/resources/images/location-ico.svg')} />
              <div className="address-notdefined">Online Event</div>
            </div>
          ) : (
            <div className={isHome ? 'event-address-block' : 'event-address'}>
              <img alt="nodata" src={asset('/resources/images/location-ico.svg')} />
              {!!address && <Address className="address-form" type="simple" {...address} />}
              {!address && <div className="address-notdefined">Not Defined</div>}
            </div>
          )}
        </div>
      </div>
      {isHome ? (
        renderStatisticsContent()
      ) : (
        <div className="table-cell">
          <div className="eventslist-actions top_position">{renderStatisticsContent()}</div>
        </div>
      )}
    </div>
  )

  return isHome ? (
    renderContent()
  ) : (
    <tr className={cs}>
      <td className="eventslist-details">{renderContent()}</td>
    </tr>
  )
}

export default EventRow
